/**
 * Razorpay Checkout Component
 * Reusable payment component for both Job Seekers and HR
 */

import React, { useState } from 'react';
import { CreditCard, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { handleRazorpayPayment } from '../../services/razorpayService.js';

const RazorpayCheckout = ({
  amount,
  planType,
  planDuration,
  planName,
  userDetails,
  onSuccess,
  onFailure,
  buttonText = 'Pay Now',
  buttonClassName = '',
  notes = {},
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handlePayment = async () => {
    setIsProcessing(true);
    setPaymentStatus('idle');
    setErrorMessage('');

    try {
      await handleRazorpayPayment(
        amount,
        planType,
        planDuration,
        userDetails,
        notes,
        (response) => {
          console.log('Payment successful:', response);
          setIsProcessing(false);
          setPaymentStatus('success');

          if (onSuccess) {
            onSuccess(response);
          }
        },
        (error) => {
          console.error('Payment failed:', error);
          setIsProcessing(false);
          setPaymentStatus('failed');
          setErrorMessage(error.message || 'Payment failed');

          if (onFailure) {
            onFailure(error);
          }
        }
      );
    } catch (error) {
      console.error('Error initiating payment:', error);
      setIsProcessing(false);
      setPaymentStatus('failed');
      setErrorMessage(error.message || 'Failed to initiate payment');

      if (onFailure) {
        onFailure(error);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Payment Button */}
      <button
        onClick={handlePayment}
        disabled={isProcessing}
        className={`
          flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold
          transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
          ${buttonClassName || 'bg-blue-600 text-white hover:bg-blue-700'}
        `}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            {buttonText}
          </>
        )}
      </button>

      {/* Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Payment Successful!</p>
            <p className="text-sm">Your subscription has been activated.</p>
          </div>
        </div>
      )}

      {paymentStatus === 'failed' && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Payment Failed</p>
            <p className="text-sm">{errorMessage || 'Please try again or contact support.'}</p>
          </div>
        </div>
      )}

      {/* Payment Details */}
      <div className="text-sm text-gray-600">
        <p>
          <span className="font-medium">Plan:</span> {planName}
        </p>
        <p>
          <span className="font-medium">Amount:</span> ₹{amount.toLocaleString('en-IN')}
        </p>
        <p>
          <span className="font-medium">Duration:</span> {planDuration}
        </p>
      </div>

      {/* Security Notice */}
      <div className="text-xs text-gray-500 border-t pt-3">
        <p>🔒 Secure payment powered by Razorpay</p>
        <p>Your payment information is encrypted and secure</p>
      </div>
    </div>
  );
};

export default RazorpayCheckout;

